import { useState } from 'react';
import axiosInstance from '../../interceptor/axiosInstance';
import { ApiRoutes } from '../../routes/routeConstants/apiRoutes';
import { deserialize, serialize } from 'serializr';
import { CategoriesList } from 'models/ScopeThreeCategories/ScopeThreeCategories.model';
import { generatePath } from 'react-router-dom';
import {
  ScopeThreeCategoryOne,
  ScopeThreeSection,
} from 'models/ScopeThreeCategoryOne/ScopeThreeCategoryOne.model';
import { Category1Constants } from 'constants/Scope3Constants/category1.constants';
import { RawMaterialPackagingMeta } from 'models/RawMaterialPackagingMeta/rawMaterialPackagingMeta.model';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/notificationTypes';
import { generateAddressLabel } from 'views/Scope3/Category4&9/LocationModal';

const addAddressLabel = (items?: ScopeThreeSection[]) =>
  items?.map(item => ({
    ...item,
    origins: item?.origins?.map(origin => ({
      ...origin,
      ...(origin?.address && {
        addressLabel: generateAddressLabel(origin.address),
      }),
    })),
  }));

const { NAME, SUCCESS_MESSAGE } = Category1Constants;

const ScopeThreeService = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [scopeThreeCategories, setScopeThreeCategories] = useState<
    CategoriesList[]
  >([]);
  const [categoryOne, setCategoryOne] = useState<ScopeThreeCategoryOne>();
  const [othersMeta, setOtherMeta] = useState<RawMaterialPackagingMeta[]>([]);

  const fetchCategories = async (locationId: number, year: number) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.CATEGORY_LISTING, {
        locationId,
        year,
      });
      const response = await axiosInstance.get(url);
      const deserializedData = deserialize(
        CategoriesList,
        response.data.scope3_status
      );
      setScopeThreeCategories(deserializedData as CategoriesList[]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategoryOne = async (locationId: number, year: number) => {
    try {
      setLoading(true);
      const url = generatePath(ApiRoutes.GET_CATEGORY_ONE, {
        locationId,
        year,
        category: NAME,
      });
      const response = await axiosInstance.get(url);
      const deserializedData = deserialize(
        ScopeThreeCategoryOne,
        response.data
      );
      const modifiedCategoryData: ScopeThreeCategoryOne = {
        ...deserializedData,
        ingredients: addAddressLabel(deserializedData?.ingredients),
        packagings: addAddressLabel(deserializedData?.packagings),
        others: addAddressLabel(deserializedData?.others),
      };
      setCategoryOne(modifiedCategoryData);
      return modifiedCategoryData;
    } finally {
      setLoading(false);
    }
  };

  const updateCategoryOne = async (
    locationId: number,
    year: number,
    payload: ScopeThreeCategoryOne
  ) => {
    try {
      setLoading(true);
      const desiredPayload = serialize(ScopeThreeCategoryOne, payload);
      const url = generatePath(ApiRoutes.GET_CATEGORY_ONE, {
        locationId,
        year,
        category: NAME,
      });
      const response = await axiosInstance.put(url, desiredPayload);
      Notification({
        message: response?.data?.message ?? SUCCESS_MESSAGE,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
      return true;
    } finally {
      setLoading(false);
    }
  };

  const fetchOtherMeta = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(ApiRoutes.META_OTHER);
      const data = deserialize(
        RawMaterialPackagingMeta,
        response.data
      ) as RawMaterialPackagingMeta[];
      setOtherMeta(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    fetchCategories,
    scopeThreeCategories,
    fetchCategoryOne,
    categoryOne,
    updateCategoryOne,
    fetchOtherMeta,
    othersMeta,
  };
};

export default ScopeThreeService;
